import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CalendarPlus,
  CalendarClock,
  Sparkles,
  Users,
  ShoppingBag,
  Home,
  CalendarRange,
  BookCheck,
  LayoutGrid,
  Settings,
  ArrowRight,
  Clock,
} from "lucide-react";
import {
  CommandDialog,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandSeparator,
  CommandShortcut,
} from "../../ui/command";
import { getAllBookings, getQuoteRequests } from "../../../services/api";
import { deriveCustomers, Customer, RawContact } from "../../../lib/customers";
import { formatSlotDate } from "../../../lib/timezone";

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const quickActions = [
  { label: "New booking", icon: CalendarPlus, href: "/control/bookings/add" },
  { label: "Block time / mark a day off", icon: CalendarClock, href: "/control/schedule?tab=time-off" },
  { label: "New service", icon: Sparkles, href: "/control/services?new=1" },
  { label: "Set service durations", icon: Clock, href: "/control/services" },
  { label: "View customers", icon: Users, href: "/control/customers" },
];

const goTo = [
  { label: "Dashboard", icon: Home, href: "/control" },
  { label: "Schedule", icon: CalendarRange, href: "/control/schedule" },
  { label: "Bookings", icon: BookCheck, href: "/control/bookings" },
  { label: "Customers", icon: Users, href: "/control/customers" },
  { label: "Services", icon: Sparkles, href: "/control/services" },
  { label: "Shop", icon: ShoppingBag, href: "/control/shop" },
  { label: "Content", icon: LayoutGrid, href: "/control/cms/gallery" },
  { label: "Settings", icon: Settings, href: "/control/settings" },
];

export function CommandPalette({ open, onOpenChange }: CommandPaletteProps) {
  const navigate = useNavigate();
  const [bookings, setBookings] = useState<RawContact[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loaded, setLoaded] = useState(false);

  // Fetch once, the first time the palette is opened. Search is then instant
  // and client-side — no per-keystroke requests.
  useEffect(() => {
    if (!open || loaded) return;
    let active = true;
    Promise.all([getAllBookings(), getQuoteRequests()])
      .then(([b, q]: any) => {
        if (!active) return;
        const bookingList: RawContact[] = b?.data?.data ?? [];
        const quoteList: RawContact[] = q?.data?.data ?? [];
        setBookings(bookingList);
        setCustomers(deriveCustomers(bookingList, quoteList));
        setLoaded(true);
      })
      .catch(() => active && setLoaded(true));
    return () => {
      active = false;
    };
  }, [open, loaded]);

  const run = (href: string) => {
    onOpenChange(false);
    navigate(href);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder="Search bookings & customers, or jump to a section…" />
      <CommandList>
        <CommandEmpty>No matches.</CommandEmpty>

        <CommandGroup heading="Quick actions">
          {quickActions.map((a) => (
            <CommandItem
              key={a.label}
              value={`action ${a.label}`}
              onSelect={() => run(a.href)}
              className="gap-3 text-[#3a2e2c]"
            >
              <a.icon className="h-4 w-4 text-[#B87775]" />
              {a.label}
            </CommandItem>
          ))}
        </CommandGroup>

        <CommandSeparator />

        {customers.length > 0 && (
          <CommandGroup heading="Customers">
            {customers.slice(0, 6).map((c) => (
              <CommandItem
                key={c.key}
                value={`customer ${c.name} ${c.email} ${c.phone}`}
                onSelect={() => run(`/control/customers?c=${encodeURIComponent(c.key)}`)}
                className="gap-3 text-[#3a2e2c]"
              >
                <Users className="h-4 w-4 text-[#9A6B69]" />
                <span className="truncate">{c.name}</span>
                <span className="ml-auto truncate text-xs text-[#9A6B69]">
                  {c.email || c.phone}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {bookings.length > 0 && (
          <CommandGroup heading="Bookings">
            {bookings.slice(0, 6).map((b) => (
              <CommandItem
                key={b.id}
                value={`booking ${b.name ?? b.booking_name ?? ""} ${b.email_address ?? ""}`}
                onSelect={() => run(`/control/bookings/view/${b.id}`)}
                className="gap-3 text-[#3a2e2c]"
              >
                <BookCheck className="h-4 w-4 text-[#9A6B69]" />
                <span className="truncate">{b.name ?? b.booking_name ?? "Booking"}</span>
                {b.booking_start_date_time && (
                  <span className="ml-auto shrink-0 text-xs tabular-nums text-[#9A6B69]">
                    {formatSlotDate(b.booking_start_date_time)}
                  </span>
                )}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        <CommandSeparator />

        <CommandGroup heading="Go to">
          {goTo.map((g) => (
            <CommandItem
              key={g.href}
              value={`goto ${g.label}`}
              onSelect={() => run(g.href)}
              className="gap-3 text-[#3a2e2c]"
            >
              <g.icon className="h-4 w-4 text-[#9A6B69]" />
              {g.label}
              <CommandShortcut>
                <ArrowRight className="h-3.5 w-3.5" />
              </CommandShortcut>
            </CommandItem>
          ))}
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
}
